import { Stack } from '@mui/material';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { BigBreadcrumbs } from '@/components/navigation/BigBreadcrumbs';
import { PageHeader } from '@/components/navigation/PageHeader';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { ArtifactStudio } from '@/modules/artifactStudio/ArtifactStudio';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useMemoPaths } from '@/modules/memos/hooks/useMemoPaths';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

/**
 * Lays out the Studio and its child pages, such as memo details. When
 * `memos_in_studio` is disabled, redirects a memo page to the memo details page
 * under Documents.
 */
export function ClientDetailsArtifactBuilderPage() {
  const householdId = useRequiredParam('householdId');
  const { memoId } = useParams();
  const isMemosInStudioEnabled = useFeatureFlag('memos_in_studio');
  const { getMemoDetailsPath } = useMemoPaths(householdId);
  if (memoId && !isMemosInStudioEnabled) {
    return <Navigate replace to={getMemoDetailsPath(memoId)} />;
  }

  const heading = memoId ? (
    <BigBreadcrumbs
      crumbs={[
        {
          label: 'Studio',
          to: getCompletePathFromRouteKey(
            ROUTE_KEYS.HOUSEHOLD_ARTIFACT_BUILDER,
            { householdId }
          ),
        },
        { label: 'Memo details' },
      ]}
    />
  ) : (
    'Studio'
  );

  return (
    <Stack height="100%" minHeight={0}>
      <PageHeader heading={heading} />
      <Stack p={2} flex={1} minHeight={0}>
        <Outlet />
      </Stack>
    </Stack>
  );
}

/** Shows the Studio composer and saved artifacts. */
export function ClientDetailsArtifactStudioPage() {
  const householdId = useRequiredParam('householdId');
  return <ArtifactStudio key={householdId} householdId={householdId} />;
}
